import type { BoundPersistenceEntry, ModulePersistenceEntry, PersistenceEntry, SliceDeclaration } from './types'

/**
 * Имена, под которыми в браузер уезжают секреты. Запись с таким полем НЕ
 * сохраняется ни при каком срок жизни (D-084, R10): токены живут в HttpOnly-cookie
 * Gateway, пароли — только в изолированной форме секрета.
 */
const SECRET_NAME = /token|password|passwd|secret|credential|authorization|cookie|api[-_]?key/i

/** Путь до первого поля с именем секрета или `null`. */
export function findSecretField(value: unknown, path = '$', seen = new Set<unknown>()): string | null {
  if (typeof value !== 'object' || value === null || seen.has(value)) return null
  seen.add(value)
  for (const [name, nested] of Object.entries(value)) {
    const at = Array.isArray(value) ? `${path}[${name}]` : `${path}.${name}`
    if (!Array.isArray(value) && SECRET_NAME.test(name)) return at
    const inner = findSecretField(nested, at, seen)
    if (inner !== null) return inner
  }
  return null
}

/** Объявить запись реестра: типизированная форма → запись модуля. */
export function definePersistenceEntry<S, V>(entry: PersistenceEntry<S, V>): ModulePersistenceEntry<S> {
  return {
    key: entry.key,
    slice: entry.slice,
    lifetime: entry.lifetime,
    scope: entry.scope,
    version: entry.version,
    ...(entry.ttlMs !== undefined ? { ttlMs: entry.ttlMs } : {}),
    pick: entry.pick,
    revive: (raw) => {
      const value = entry.decode(raw)
      return value === undefined ? undefined : entry.restore(value)
    },
  }
}

/**
 * Привязать записи модуля к RootState: `select` берёт срез модуля, `pick` —
 * поле из него. Пакет так и не узнаёт формы приложения (D-051). Имя слайса
 * получает префикс модуля, как и его объявление (`qualifySlices`).
 */
export function bindPersistenceEntries<R, S>(
  moduleId: string,
  selectModule: (root: R) => S,
  entries: readonly ModulePersistenceEntry<S>[],
): BoundPersistenceEntry<R>[] {
  return entries.map(({ pick, slice, ...rest }) => ({
    ...rest,
    slice: `${moduleId}.${slice}`,
    select: (root: R) => pick(selectModule(root)),
  }))
}

/** Имена слайсов модуля с его префиксом: `platform.resources`. */
export function qualifySlices(moduleId: string, slices: readonly SliceDeclaration[]): SliceDeclaration[] {
  return slices.map((slice) => ({ ...slice, name: `${moduleId}.${slice.name}` }))
}

/**
 * Проверка реестра при сборке приложения: уникальные ключи, допустимый срок,
 * срок записи совпадает с объявленным сроком её слайса (значит, `screen` и
 * `account` в браузер не попадают) и нет имён секретов в ключах.
 * Ошибка здесь роняет запуск и тест composition, а не тихо пишет лишнее.
 */
export function validatePersistenceRegistry<R>(
  entries: readonly BoundPersistenceEntry<R>[],
  slices: readonly SliceDeclaration[],
): void {
  const names = new Set<string>()
  for (const slice of slices) {
    if (names.has(slice.name)) throw new Error(`Слайс «${slice.name}» объявлен дважды`)
    names.add(slice.name)
  }
  const keys = new Set<string>()
  for (const entry of entries) {
    if (!/^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*)+$/.test(entry.key)) {
      throw new Error(`Ключ записи «${entry.key}» должен иметь вид <пакет>.<что>`)
    }
    if (keys.has(entry.key)) throw new Error(`Запись «${entry.key}» объявлена дважды`)
    keys.add(entry.key)
    if (entry.lifetime !== 'session' && entry.lifetime !== 'device') {
      throw new Error(`Запись «${entry.key}»: в браузер пишутся только session/device, не ${String(entry.lifetime)}`)
    }
    if (!Number.isInteger(entry.version) || entry.version < 1) {
      throw new Error(`Запись «${entry.key}»: версия формы — целое число от 1`)
    }
    if (SECRET_NAME.test(entry.key)) {
      throw new Error(`Запись «${entry.key}»: секреты не сохраняются в браузере`)
    }
    const owner = slices.find((slice) => slice.name === entry.slice)
    if (owner === undefined) {
      throw new Error(`Запись «${entry.key}»: слайс «${entry.slice}» не объявлен (SliceDeclaration)`)
    }
    if (owner.lifetime !== entry.lifetime) {
      throw new Error(
        `Запись «${entry.key}»: слайс «${owner.name}» объявлен ${owner.lifetime}, а запись — ${entry.lifetime}`,
      )
    }
  }
}
